// Dropdown for adding extra accommodation items (e.g. extra bed for child)

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAccommodation } from "../../AccommodationProvider";
import type { CategoryOptionDto } from "../../../../types";
import { AssignmentType } from "../../types/accommodation.types";
import {
  getAvailableOccupancyOptions,
} from "../../utils/occupancyOptions";

/** Standard hotel VAT rate in Poland */
const DEFAULT_ACCOMMODATION_VAT_RATE = 8;

interface AddExtraDropdownProps {
  offerId: string;
  dayId: string;
  /** Category option IDs already assigned on this day - these are filtered out */
  existingItemCategoryOptionIds: string[];
  onClose: () => void;
  onAdded: () => Promise<void>;
}

/**
 * Two-phase dropdown for adding accommodation items from the hotel catalog.
 *
 * Phase 1: Shows list of available CategoryOption entries of type "accommodation".
 * Phase 2: Mini-form for selecting occupancy type, persons per room, and room quantity.
 *
 * Extended scope: handles both dostawki (extras) and main accommodation items
 * with occupancyType/personsCount — name kept for backward compatibility.
 */

type DropdownPhase =
  | { phase: "select-option" }
  | { phase: "configure-occupancy"; selectedOption: CategoryOptionDto };

export const AddExtraDropdown: React.FC<AddExtraDropdownProps> = ({
  offerId,
  dayId,
  existingItemCategoryOptionIds,
  onClose,
  onAdded,
}) => {
  const {
    addAccommodationExtra,
    getAccommodationOptions,
    addToAllDays,
    accommodationDays,
    guestStructure,
  } = useAccommodation();

  const [options, setOptions] = useState<CategoryOptionDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Two-phase dropdown state
  const [dropdownPhase, setDropdownPhase] = useState<DropdownPhase>({
    phase: "select-option",
  });

  // Phase 2 form state
  const [occupancyType, setOccupancyType] = useState<AssignmentType>("pairs");
  const [personsCount, setPersonsCount] = useState(1);
  const [roomQuantity, setRoomQuantity] = useState(1);

  const occupancySelectRef = useRef<HTMLSelectElement>(null);
  const optionListRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const data = await getAccommodationOptions();
        setOptions(data);
      } catch (error) {
        setFetchError("Nie udało się pobrać opcji dostawek");
      } finally {
        setIsLoading(false);
      }
    };

    fetchOptions();
  }, [getAccommodationOptions]);

  // Focus the occupancy select when entering phase 2
  useEffect(() => {
    if (dropdownPhase.phase === "configure-occupancy") {
      occupancySelectRef.current?.focus();
    }
  }, [dropdownPhase.phase]);

  // Compute available occupancy options once per render (memoized by guestStructure)
  const availableOccupancyOptions = useMemo(
    () => getAvailableOccupancyOptions(guestStructure),
    [guestStructure]
  );

  const handleOptionClick = (option: CategoryOptionDto) => {
    // Set default occupancy type to first available
    if (availableOccupancyOptions.length > 0) {
      const firstOption = availableOccupancyOptions[0];
      setOccupancyType(firstOption.value);
      setPersonsCount(firstOption.personsPerRoom);
    }

    setRoomQuantity(1);
    setSubmitError(null);
    setDropdownPhase({ phase: "configure-occupancy", selectedOption: option });
  };

  const handleOccupancyTypeChange = (newType: AssignmentType) => {
    setOccupancyType(newType);
    // Reset personsCount to matching option's default
    const matchingOption = availableOccupancyOptions.find((o) => o.value === newType);
    if (matchingOption) {
      setPersonsCount(matchingOption.personsPerRoom);
    }
  };

  const handleCancelPhase2 = () => {
    // Go back to option list — does NOT close the dropdown
    setDropdownPhase({ phase: "select-option" });
    // Move focus to the first option button for keyboard users
    setTimeout(() => {
      optionListRef.current?.focus();
    }, 0);
  };

  const handleSubmit = async () => {
    if (dropdownPhase.phase !== "configure-occupancy") return;

    const option = dropdownPhase.selectedOption;
    setIsAdding(true);
    setSubmitError(null);

    try {
      const extra = {
        categoryOptionId: option.id,
        quantity: roomQuantity,
        unitPrice: option.defaultUnitPrice || 0,
        vatRate: option.vatRate ?? DEFAULT_ACCOMMODATION_VAT_RATE,
        occupancyType,
        personsCount:
          occupancyType === "singles_shared" ? personsCount : undefined,
      };

      if (addToAllDays) {
        const nights = accommodationDays.slice(0, -1); // bez checkout dnia
        for (const day of nights) {
          await addAccommodationExtra(day.id, extra);
        }
      } else {
        await addAccommodationExtra(dayId, extra);
      }

      await onAdded();
      onClose();
    } catch (error) {
      setSubmitError("Nie udało się dodać dostawki. Spróbuj ponownie.");
    } finally {
      setIsAdding(false);
    }
  };

  if (isLoading) {
    return (
      <div
        className="text-sm text-gray-500"
        data-testid="add-extra-loading"
        aria-live="polite"
      >
        Ładowanie opcji...
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="text-sm text-red-500" data-testid="add-extra-error">
        {fetchError}
        <button
          type="button"
          onClick={onClose}
          className="ml-2 text-gray-400 hover:text-gray-600"
          aria-label="Zamknij"
        >
          ✕
        </button>
      </div>
    );
  }

  const availableOptions = options.filter(
    (opt) => !existingItemCategoryOptionIds.includes(opt.id)
  );

  if (availableOptions.length === 0) {
    return (
      <div className="text-sm text-gray-500">
        Brak dostępnych dostawek
        <button
          type="button"
          onClick={onClose}
          className="ml-2 text-gray-400 hover:text-gray-600"
          aria-label="Zamknij"
        >
          ✕
        </button>
      </div>
    );
  }

  // Render the two-phase dropdown
  return (
    <div
      className="border border-gray-200 rounded-lg bg-white shadow-sm p-3"
      data-testid="add-extra-dropdown"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">
          {dropdownPhase.phase === "select-option"
            ? "Wybierz:"
            : "Szczegóły obsady:"}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-sm"
          aria-label="Zamknij"
        >
          ✕
        </button>
      </div>

      {dropdownPhase.phase === "select-option" && (
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {availableOptions.map((option, index) => (
            <button
              key={option.id}
              ref={index === 0 ? optionListRef : undefined}
              type="button"
              onClick={() => handleOptionClick(option)}
              disabled={isAdding}
              className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
              data-testid={`add-extra-option-${option.id}`}
            >
              <span className="font-medium">{option.name}</span>
              {option.defaultUnitPrice > 0 && (
                <span className="ml-2 text-xs text-gray-500">
                  {new Intl.NumberFormat("pl-PL", {
                    style: "currency",
                    currency: "PLN",
                    currencyDisplay: "code",
                    maximumFractionDigits: 0,
                  }).format(option.defaultUnitPrice)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {dropdownPhase.phase === "configure-occupancy" && (
        <div className="space-y-3">
          {/* Header with room type name */}
          <div
            className="text-sm font-medium text-gray-800"
            data-testid="occupancy-form-header"
          >
            {dropdownPhase.selectedOption.name} — szczegóły obsady
          </div>

          {availableOccupancyOptions.length === 0 ? (
            <div
              className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2"
              data-testid="no-guest-structure-message"
            >
              Brak zdefiniowanej struktury gości. Wróć do panelu noclegów i
              ustaw pary/singli.
              <button
                type="button"
                onClick={onClose}
                className="block mt-2 text-xs text-yellow-600 hover:text-yellow-800"
              >
                Wróć
              </button>
            </div>
          ) : (
            <>
              {/* Dla kogo */}
              <div>
                <label
                  htmlFor="occupancy-type-select"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Dla kogo:
                </label>
                <select
                  id="occupancy-type-select"
                  ref={occupancySelectRef}
                  value={occupancyType}
                  onChange={(e) =>
                    handleOccupancyTypeChange(e.target.value as AssignmentType)
                  }
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  data-testid="occupancy-type-select"
                >
                  {availableOccupancyOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Osób na pokój — only for singles_shared */}
              {occupancyType === "singles_shared" && (
                <div>
                  <label
                    htmlFor="persons-per-room-input"
                    className="block text-xs font-medium text-gray-700 mb-1"
                  >
                    Osób na pokój:
                  </label>
                  <input
                    id="persons-per-room-input"
                    type="number"
                    min="1"
                    value={personsCount}
                    onChange={(e) =>
                      setPersonsCount(
                        Math.max(1, parseInt(e.target.value, 10) || 1)
                      )
                    }
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    data-testid="persons-per-room-input"
                  />
                </div>
              )}

              {/* Ilość pokoi */}
              <div>
                <label
                  htmlFor="room-quantity-input"
                  className="block text-xs font-medium text-gray-700 mb-1"
                >
                  Ilość pokoi:
                </label>
                <input
                  id="room-quantity-input"
                  type="number"
                  min="1"
                  value={roomQuantity}
                  onChange={(e) =>
                    setRoomQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))
                  }
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                  data-testid="room-quantity-input"
                />
              </div>

              {/* Inline submit error */}
              {submitError && (
                <div className="text-sm text-red-500" data-testid="submit-error">
                  {submitError}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isAdding}
                  className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                    isAdding
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                  data-testid="occupancy-form-submit"
                >
                  {isAdding ? "Dodaję..." : "Dodaj"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelPhase2}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                  data-testid="occupancy-form-cancel"
                >
                  Anuluj
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
